// Updated: 2026-09-13
import type { PublicProfileResponse, BanScope, SecureProfileResponse } from '@mrb91002/shared-types'
import { useQuery, useQueryClient, skipToken, type QueryClient } from '@tanstack/react-query'
import { getCurrentUser, AuthUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth'
import { createContext, useContext, useState, useEffect, ReactNode, Context, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchOwnSecureProfile } from '../api/fetchers/profile'
import { registerTokenRefreshCallback } from '../api/secure/helpers'
// ONLY `keys` is imported from the profile query module, never `queries`. `queries/profile/keys.ts` has
// ZERO imports, so pulling it in here cannot re-enter this file; `queries/profile/queries.ts:97` calls
// `useAuth()`, so importing THAT module would close a cycle back through AuthContext.
import { profileKeys } from '../queries/profile/keys'
// A pure predicate with no imports of its own, so it cannot re-enter this file.
import { isUsableScreenName } from '../utils/screenName'

/*
 * ⚠️  CRITICAL TODO: FIX COGNITO LOGOUT CONFIGURATION ⚠️
 *
 * PROBLEM: Global logout (signOut({ global: true })) fails with:
 * "Required String parameter 'redirect_uri' is not present"
 *
 * CURRENT WORKAROUND: Using local-only logout (global: false)
 *
 * SECURITY IMPACT: Users remain logged in on other devices/browsers
 *
 * ROOT CAUSE: Mismatch between Cognito logout URL parameters
 * - Cognito expects 'redirect_uri' but receives 'logout_uri'
 * - CDK logoutUrls config vs Amplify OAuth logout implementation
 *
 * INVESTIGATION NEEDED:
 * 1. Check CDK Cognito construct logout URL configuration
 * 2. Verify Amplify OAuth logout parameter mapping
 * 3. Test with different Amplify signOut options
 * 4. Consider custom logout URL construction
 *
 * PRIORITY: HIGH - Affects user security across devices
 *
 * ERROR DETAILS:
 * URL: https://dev-mbenav-pattern-warehouse-v3.auth.us-west-2.amazoncognito.com/error
 * Params: client_id=5gh30bpinv1cefcrm4d3b09ugs&logout_uri=https%3A%2F%2Flocalhost%3A3000%2F
 * Expected: redirect_uri parameter instead of logout_uri
 */

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  error: string | null
  accessToken: string | null
  idToken: string | null
  session: Record<string, unknown> | null
  screen_name: string | null
  setScreenName: (sn: string | null) => void
  profile: PublicProfileResponse | null
  setProfile: (profile: PublicProfileResponse | null) => void
  restrictedActions: BanScope[]
  isRestricted: boolean
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getIdToken: () => Promise<string | null>
  getAccessToken: () => Promise<string | null>
  getCurrentSession: () => Promise<Record<string, unknown> | null>
  reloadProfile: () => Promise<void>
}

const AuthContext: Context<AuthContextType | undefined> = createContext<AuthContextType | undefined>(undefined)

/**
 * THE PERSONAL_PURGE — BL-002. ONE eviction, in ONE place, named by PREFIX (Req 3.1, 3.2).
 *
 * `['me']` is named EXACTLY and nothing is enumerated. Every personal key in the app is `'me'`-namespaced
 * by convention, so prefix matching reaches all eleven families today — `['me','materialReview',…]`,
 * `['me','review','pattern',…]`, `['me','projectDraft',…]`, `['me','projectTemp',…]`,
 * `['me','materialDiscovery',…]`, `['me','profile',…]`, `['me','collections',…]`,
 * `['me','notifications','unreadCount']`, `['me','notifications','list',…]`, `['me','relations']`,
 * `['me','craftRoom']` — and, more importantly, every family a LATER slice adds inherits the eviction with
 * no purge site of its own (Req 2.6). An enumeration would omit each of those silently, and that omission
 * IS the recurrence BL-002 asks to close.
 *
 * It is a MODULE function, deliberately not a member of the auth context value: the Auth_Surface is
 * exactly nineteen names (Req 1.1, 1.2) and attaching this would widen it. That is asserted, not trusted —
 * Property 8 in `__tests__/AuthContext.test.tsx` compares `Object.keys(contextValue).sort()` against the
 * literal nineteen, and attaching this helper is its named mutation (M5).
 */
function purgePersonalCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: ['me'] })
}

/**
 * The `restrictedActions` empty value, hoisted to MODULE scope and frozen so that every render in which
 * the Profile_Entry carries no `permissions` — and every render in which the Derive_Guard is closed —
 * yields the SAME ARRAY IDENTITY rather than a fresh `[]`. Paired with the `useMemo` at the derivation,
 * this makes `restrictedActions` referentially stable across renders that did not change it.
 *
 * This is PROPHYLACTIC, and is labelled so rather than claimed as a fix: no consumer keys a dependency
 * array on `restrictedActions` (or on `profile`) today. Every reader passes it straight into
 * `isScopeRestricted` at render time — `Navigation.tsx:27-28`, `FlagButton.tsx:103`,
 * `AppealButton.tsx:18`, `CommentsReviews.tsx:167`, `PatternEditor.tsx:94` — and a search for either name
 * inside a dependency array returns nothing. So nothing re-fires today either way; what this buys is that
 * the first consumer to key a `useEffect`/`useMemo` on it does not inherit a per-render identity churn.
 *
 * Frozen rather than a bare `[]` because this value is handed to consumers through the context: a mutation
 * of it would otherwise be a mutation of the shared empty state. `ZERO_COUNTS` in
 * `hooks/useNotifications.ts:70` is the same pattern.
 *
 * It stays typed `BanScope[]` and not `readonly BanScope[]`, which is why the freeze is a SEPARATE
 * STATEMENT rather than an initialising `Object.freeze([])` — that returns `readonly never[]` and would
 * force a cast. `AuthContextType.restrictedActions` is `BanScope[]`, and the Auth_Surface's nineteen member
 * TYPES are frozen for this slice along with its nineteen names (Requirements 1.1, 1.2), so widening the
 * member to a readonly array is not available here. The freeze is therefore a runtime guarantee the
 * compiler does not restate.
 */
const EMPTY_RESTRICTED_ACTIONS: BanScope[] = []
Object.freeze(EMPTY_RESTRICTED_ACTIONS)

/** The one localStorage key the screen-name cursor bootstraps from. */
const CACHED_SCREEN_NAME_KEY = 'fpw_screen_name'

/**
 * Read the cached screen-name cursor — THE ONLY UNTRUSTED SOURCE the cursor has — and refuse a value
 * that is not a name.
 *
 * `localStorage` holds strings and nothing else, so a nullish value stored by ANY earlier build of this
 * app is now the four-character string `'null'`, and it survives every deploy in a long-lived browser
 * profile. Adopted as a screen name it satisfies every truthiness gate downstream, and
 * `encodeURIComponent('null')` builds a perfectly well-formed request path out of it — which is how
 * `GET /api/secure/craft-room/null` came to be issued, and retried, during profile creation, while
 * `Layout` was logging `screenName null` in the same breath. The three `setScreenName` WRITES in this
 * file are all truthiness-guarded already; the READS were not, so this is the read.
 *
 * A poisoned entry is REMOVED rather than merely ignored, so the session does not re-read it on the next
 * failure arm and so the browser profile stops carrying it forward.
 */
function readCachedScreenName(): string | null {
  const cached = localStorage.getItem(CACHED_SCREEN_NAME_KEY)
  if (cached === null) return null
  if (isUsableScreenName(cached)) return cached
  localStorage.removeItem(CACHED_SCREEN_NAME_KEY)
  return null
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  /**
   * ─── THE NAVIGATE LATEST-REF, AND WHY `initAuth` READS NAVIGATION THROUGH IT (BL-053) ───────────
   *
   * `initAuth`'s effect must run ONCE PER PAGE LOAD. It previously listed `navigate` as a dependency,
   * for the single reason that two of its arms redirect to `/create/profile`. In react-router 7.18.1
   * `useNavigate` returns a `useCallback` whose deps include `locationPathname` and `routePathnamesJson`
   * (`react-router/dist/development/chunk-KS7C4IRE.mjs:5954-5960`), so `navigate` TAKES A NEW IDENTITY ON
   * EVERY NAVIGATION. The effect therefore re-ran on every route change, and every signed-in user paid a
   * `fetchAuthSession` + `getCurrentUser` + `GET /profile?self=true` per navigation. Worse on the
   * profile-required path: that arm navigates itself, so the effect re-entered and `initAuth` ran twice
   * for one mount. That was BL-053.
   *
   * Holding `navigate` here lets the effect's dep array reduce to the stable `[getCurrentSession]` while
   * both redirects still fire at the same point in the same arms. `navigate` is DELIBERATELY NOT A DEP of
   * that effect — see the note on the dep array itself, which is the line a future editor will touch.
   *
   * ⚠️ UPDATED IN AN EFFECT, NOT ASSIGNED DURING RENDER. Assigning `ref.current` during render mutates
   * state in a phase React may discard or replay (StrictMode double-render, an abandoned concurrent
   * attempt), so the standard latest-ref idiom commits the write. `useEffect` and not `useLayoutEffect`
   * because nothing reads this ref during layout: the only readers are `initAuth`'s two redirects, which
   * sit after `await`s and therefore run in a later macrotask than ANY effect of the same commit. The
   * initializer is `navigate` rather than a null placeholder, which is what makes the ref non-null for the
   * whole of the first `initAuth` run and keeps the call sites free of a `?.` that could silently skip a
   * redirect.
   *
   * The freshness this maintains is a guard for future edits rather than a live requirement, and the
   * reason is worth recording so nobody removes the effect as dead: BOTH current call sites pass an
   * ABSOLUTE path, and `resolvePath` resolves an absolute `to` against `"/"` regardless of the current
   * pathname (`chunk-KS7C4IRE.mjs:883-884`), while the underlying `navigator` comes from context and is
   * itself stable. A stale identity would route these two calls identically. Change either call to a
   * RELATIVE path and that stops being true, at which point this effect is what keeps it correct.
   */
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [idToken, setIDToken] = useState<string | null>(null)
  const [session, setSession] = useState<Record<string, unknown> | null>(null)

  /**
   * ─── THE DERIVATION BLOCK ───────────────────────────────────────────────────────────────────────
   *
   * `profile`, `restrictedActions` and `isRestricted` no longer have `useState` slices. They are
   * FUNCTIONS OF THE PROFILE_ENTRY `['me','profile', <own screen name>]`, computed at render time, and
   * that entry is the SINGLE SOURCE — not a second copy (Requirements 1.1, 4.11). Three pieces make that
   * work, and two of them are load-bearing rather than stylistic; each says below why it is there.
   */

  /**
   * 1. THE SCREEN_NAME_CURSOR — today's `screen_name` `useState`, with a WIDENED JOB. Not a new store,
   * not a second copy of anything: the same slice, written by exactly the same lines that write it today.
   *
   * Beyond holding a value it now does two jobs:
   *   (a) it SELECTS WHICH ENTRY the provider observes (the Derive_Observer's key is built from it), and
   *   (b) it is the FALLBACK for the `screen_name` member when the payload has none (Requirement 4.12).
   *
   * (b) matters more than it looks, because the cursor IS ALREADY the `localStorage` fallback — `initAuth`
   * bootstraps it from `fpw_screen_name` before its fetch, the non-404 failure arm retains that cached name
   * through a network error, and the token-expiry catch re-reads it. So "cursor as fallback" and
   * "`localStorage` as fallback" are the same
   * sentence, and NO NEW `localStorage` READ AND NO NEW BRANCH is introduced by the derivation
   * (Requirements 9.3, 4.12). Design § 4b tabulates all ten writing branches against today's behavior.
   *
   * ONLY THE VALUE BINDING IS RENAMED, and the SETTER KEEPS TODAY'S NAME — `setScreenName` is the second
   * element of this very `useState`, not an alias of one. So all TEN `setScreenName` call sites —
   * `signOut`'s, `initAuth`'s seven, and `reloadProfile`'s two — survive BYTE-FOR-BYTE, which is the whole
   * reason the rename is safe, and design § 4c's `setScreenName` Setter_Shim is satisfied by this line
   * alone: it IS today's setter, under today's name, and is therefore EXACTLY FAITHFUL for every input by
   * construction rather than by argument. Its only new consequence is that the cursor also selects the
   * observed entry, and since the only key `profileKeys.myProfile` is ever WRITTEN with is the signed-in
   * user's own screen name, pointing the cursor anywhere else observes an entry that cannot exist.
   *
   * ⚠️ DO NOT SPLIT THIS INTO `setScreenNameCursor` PLUS `const setScreenName = setScreenNameCursor`.
   * Design § 4b spells the setter `setScreenNameCursor` and § 4c writes the alias as a separate line; that
   * is a NAMING PREFERENCE, and taking it literally COSTS THREE `react-hooks/exhaustive-deps` WARNINGS.
   * eslint knows a `useState` setter is stable and omits it from the required deps; it does NOT know that
   * of a `const` bound to one, so the alias makes `setScreenName` a "missing dependency" at `signOut`'s
   * `useCallback`, at `initAuth`'s effect and at `reloadProfile`'s `useCallback`. Two of the three are
   * UNFIXABLE INSIDE THIS SLICE — the fix is adding a dep to a frozen array, which Requirement 1.3
   * forbids — so they would ship as permanent noise on frozen code, on a file that carried ZERO warnings
   * at HEAD. Destructuring the name directly satisfies every constraint the spec actually cares about:
   * the ten call sites are byte-identical, the state is still the cursor, `screenNameCursor` keeps its
   * name, and the Auth_Surface member `setScreenName` keeps its name, position and type.
   *
   * `setProfile` is a different animal — it WRITES the cache and its functional form is mandatory
   * (design § 4) — so it cannot collapse into a declaration this way and appears below on its own.
   */
  const [screenNameCursor, setScreenName] = useState<string | null>(null)

  /**
   * 2. THE DERIVE_OBSERVER — a PASSIVE `useQuery`. It never fetches. `initAuth`'s establishing write and
   * `reloadProfile`'s refresh remain the only two things in this provider that touch the network for this
   * entry. Its entire job is to subscribe the provider to the entry, so that a `setQueryData` write
   * RE-RENDERS the provider and the three derived members below recompute.
   *
   * ⚠️ BOTH `queryFn: skipToken` AND `enabled: false` are written, and the honest reason is NOT the one
   * design § 4b gives. § 4b (its "finding 5") argues that `enabled: false` is REQUIRED because `skipToken`
   * is invisible to `isActive()` (`query.js:97-101`) and to `isStale` (`queryObserver.js:460-462`), so that
   * `skipToken` alone would let the Personal_Purge's `removeQueries` satisfy `shouldFetchOptionally`
   * (`queryObserver.js:457-459`) and provoke a fetch that `ensureQueryFn` (`utils.js:225-238`) turns into a
   * `console.error`. MEASURED AGAINST THE INSTALLED 5.101.3, THAT MECHANISM DOES NOT HOLD:
   *
   *   - `defaultQueryOptions` NORMALISES `skipToken` INTO THE GATE — `queryClient.js:280-282`:
   *     `if (defaultedOptions.queryFn === skipToken) { defaultedOptions.enabled = false }`. It runs after
   *     the caller's spread, so it wins.
   *   - Every path this file can take is defaulted: `QueryObserver.setOptions` calls
   *     `defaultQueryOptions` itself (`queryObserver.js:86`), and so does `useBaseQuery.js:31`.
   *   - So `observer.options.enabled` is ALREADY `false` for a `skipToken` query. The two predicates do
   *     read `options.enabled` as § 4b says — but what they read is the flag `skipToken` was translated
   *     into, not an unset one. `skipToken` is not invisible to them; it IS them.
   *   - Measured, purge-then-re-render, against the installed build: with a REAL `queryFn` and no
   *     `enabled`, the removal provokes a second fetch (1 → 2 calls) — so § 1's refetch-after-purge hazard
   *     is real and the harness detects it. With `skipToken` ALONE: 0 fetches, 0 `console.error`,
   *     `isActive() === false`, `isDisabled() === true`, `fetchStatus: 'idle'`, `errorUpdateCount: 0` —
   *     byte-for-byte the same reading as `skipToken` + `enabled: false`.
   *
   * CONSEQUENCES, recorded rather than smoothed over. (a) `enabled: false` is REDUNDANT here, not
   * required; it is kept as DEFENCE IN DEPTH — it states the gate at this call site instead of delegating
   * it to a normalisation step inside `defaultQueryOptions`, and it is the line that keeps this observer
   * passive if `skipToken` is ever removed. (b) M11 (drop to `skipToken` alone) will NOT produce the
   * failure § 4b names; it is behaviourally indistinguishable here, so it cannot be reported as a red.
   * (c) The "keep `skipToken` so a future gate flip fails LOUDLY" rationale is false for the same reason:
   * `enabled: true` alongside `skipToken` is forced back to `false` by the same three lines, measured —
   * the flip is silent and inert, not loud. What actually makes a gate flip loud is removing `skipToken`,
   * because only then does a real `queryFn` exist to run.
   */
  const { data } = useQuery<SecureProfileResponse>({
    queryKey: profileKeys.myProfile(screenNameCursor ?? ''),
    queryFn: skipToken,
    enabled: false,
  })

  /**
   * 3. THE DERIVE_GUARD — one line, and every member below reads `cachedOwnProfile`, NEVER `data`. Same
   * shape as `UserRelationsContext.tsx:97` and `CraftRoomContext.tsx:173`.
   *
   * ⚠️ MANDATORY, and ONE BRANCH IS WHAT MAKES IT SO. `.kiro/steering/react-query-conventions.md` § slice 7
   * rule 1: TWO mechanisms are required and NEITHER IS SUFFICIENT ALONE — the guard blanks the surface on
   * every render where the gate is closed INCLUDING THE PRE-EFFECT ONE, and the Personal_Purge guarantees
   * nothing stale survives to be served when the gate next opens. `enabled: false` does not evict, and
   * neither does a closed gate: `data` can still be the previous session's payload.
   *
   * The load-bearing branch is the TOKEN-EXPIRY / NOT-AUTHENTICATED path in `initAuth`'s outer catch.
   * There `setIsAuthenticated(false)` closes the gate while the line a few statements later DELIBERATELY
   * LEAVES THE CURSOR NON-NULL — it re-reads `fpw_screen_name` so the user sees a familiar state before
   * re-login. So the key does not move, the entry (if any) is still observed, and NOTHING BUT THIS GUARD
   * blanks `profile` and `restrictedActions` there. An unguarded derivation would serve a full profile,
   * `restricted_actions` included, to a session that is not authenticated.
   *
   * It is NOT `signOut` that makes the guard necessary, and saying so is the difference between an
   * argument and ceremony: there `setIsAuthenticated(false)` and `setScreenName(null)` are in ONE
   * SYNCHRONOUS BATCH, so the cursor coincidentally re-keys to `['me','profile','']` in the very commit
   * that closes the gate, and the surface would have blanked without a guard. Same for the
   * no-`screen_name` arm, the 404 arm and `reloadProfile`'s failure arm — all null the cursor. The
   * non-404 failure arm leaves it non-null but writes no entry. Expiry is the one path with a non-null
   * cursor AND a possible entry. Design § 4b tabulates all six.
   *
   * That window is empty in production TODAY, by two accidents — the batching coincidence at `signOut`,
   * and a `QueryClient` built fresh per page load (`index.tsx:28`) against a mount-only `initAuth`. Both
   * are accidents of control flow that this slice freezes rather than relies on, and the conventions
   * file's own subject is an SSR-SEEDABLE data layer, under which a cache populated before the first
   * render stops being hypothetical. A test constructs the window today with no artificial gate-holding —
   * seed `['me','profile','userA']`, put `fpw_screen_name=userA` in `localStorage`, drive the expiry path.
   * That is Property 9; removing this line is mutation M12.
   *
   * Requirement 8.9's no-derive-guard argument was CORRECT for the provider Stage 1 shipped and is
   * WITHDRAWN HERE BY ITS OWN TERMS — Requirement 8.10's trigger has fired, because three members now
   * read the cache. See the transition effect's docblock, which records the withdrawal at the site that
   * used to carry the argument.
   */
  const cachedOwnProfile = isAuthenticated ? data : undefined

  const profile = cachedOwnProfile?.profile ?? null
  /**
   * `permissions?: DerivedPermissionFlags` lives on `SecureProfileResponse` and nowhere else
   * (`packages/shared-types/src/api/user.ts:178`), which is why the establishing write stores the WHOLE
   * response. The `?? EMPTY_RESTRICTED_ACTIONS` produces exactly what the
   * `permissions ?? { restricted_actions: [], is_restricted: false }` default produced at the two call
   * sites that used to apply it — the default moved from write time to read time, unchanged in value.
   * `useMemo` for the identity stability described at `EMPTY_RESTRICTED_ACTIONS`; prophylactic.
   */
  const restrictedActions = useMemo(
    () => cachedOwnProfile?.permissions?.restricted_actions ?? EMPTY_RESTRICTED_ACTIONS,
    [cachedOwnProfile],
  )
  const isRestricted = restrictedActions.length > 0
  /**
   * PAYLOAD FIRST, CURSOR SECOND — and `||`, NOT `??`, matching the existing idiom at `reloadProfile`'s
   * `setScreenName(loadedProfile?.screen_name || null)`. An empty-string payload `screen_name` therefore
   * falls through to the cursor instead of surfacing `''`, which is strictly closer to today: the
   * `if (loadedProfile?.screen_name)` truthiness guard in `initAuth` means such a payload never reaches
   * the success arm at all, so no branch can produce it.
   *
   * The ORDERING is what closes the divergence. When payload and cursor disagree — the only way being an
   * independent write to the entry, e.g. a `useMyProfilePageQuery` refetch — the PAYLOAD wins, which is
   * the entire point of making the entry authoritative. Dropping the cursor fallback is mutation M13; the
   * branches it turns red are the non-404 failure arm with a cached name, and the expiry path.
   */
  const screen_name = cachedOwnProfile?.profile?.screen_name || screenNameCursor

  /**
   * ─── THE `setProfile` SETTER_SHIM (design § 4c) ─────────────────────────────────────────────────
   *
   * `setProfile` keeps its NAME, its POSITION in the value object and its DECLARED TYPE
   * (`AuthContextType.setProfile` above), so the Auth_Surface stays at exactly NINETEEN members,
   * Property 8's literal name list is unchanged, and no consumer file and no `useAuth` mock moves
   * (Requirements 1.1, 1.5). `setScreenName` is the other Setter_Shim and is satisfied by its `useState`
   * declaration in the derivation block above; this is the one that needs a body.
   *
   * ⚠️ WHY THE MEMBER STAYS IS THE COMPILER, NOT BLAST RADIUS — and the distinction matters, because
   * "we kept it to be safe" invites the next reader to drop it. Its only external consumer is DEAD CODE:
   * `components/auth/CallbackHandler.tsx:11` destructures `setProfile` and `setScreenName`, and that file
   * is unreachable — its ONLY reference anywhere in the app is the barrel re-export at
   * `components/index.ts:21`, which nothing imports, while `/callback` routes to
   * `components/pages/Callback.tsx` (`App.tsx:8`, `:150`). (`EditProfile.tsx:20`'s `setProfile` is its own
   * local `useState`, not this member.) So narrowing the surface to seventeen would touch ZERO live
   * consumer files — the order-of-magnitude argument that freezes the READERS does not reach these two.
   *
   * What keeps them is that removing either member breaks that destructure, which is a TYPE ERROR, so
   * `npm run ci`'s typecheck fails. Clearing it means deleting or editing `CallbackHandler.tsx` inside
   * this slice, which Requirement 1.5 forbids and which is already scheduled as its own backlog item
   * (design § 7's BL-051 — that entry, not this one, updates Property 8's list to seventeen). The
   * narrowing is therefore UNAVAILABLE here, not merely undesirable.
   *
   * ⚠️ THE FUNCTIONAL UPDATER IS MANDATORY (Requirement 4.5), per design § 4's per-writer table — which
   * decides on what a writer WRITES, not on how many writers exist (there are three):
   *
   *   | Writer                              | Writes                     | Form                 |
   *   |-------------------------------------|----------------------------|----------------------|
   *   | `initAuth`'s establishing write     | the WHOLE response         | plain value          |
   *   | `reloadProfile`'s refresh           | the WHOLE response         | plain value          |
   *   | THIS shim                           | ONE field, `profile`       | functional MANDATORY |
   *
   * The other two REPLACE the entire authoritative payload, so last-write-wins is the intended semantics.
   * This one PATCHES ONE FIELD of a payload it did not fetch — the steering slice-8 hazard exactly, where
   * a value write closing over a render-time snapshot would discard the other writers' edits — and the
   * functional form is also the ONLY form under which "no entry ⇒ no-op" is expressible without
   * fabricating the payload's other fields.
   *
   * ⚠️ DO NOT REINTRODUCE A PRIVATE `useState` HERE, for this member or for `profile`. A second store that
   * takes precedence over the cache is the two-store design this stage exists to REMOVE; **M14** is
   * exactly that alternative, so it is checked by a red test rather than left to argument.
   *
   * ─── THE TWO DIVERGENCES, WHICH ARE EXPECTATIONS AND NOT DEFECTS ────────────────────────────────
   *
   * `setProfile` CANNOT be exactly faithful. Property 11 ASSERTS both of the following, so neither can be
   * rediscovered later as a bug:
   *
   *   D1 — WITH NO ENTRY AT THE CURSOR, `setProfile(nonNull)` IS A NO-OP, where today it made `profile`
   *   non-null. Unavoidable, and the reason is a type fact: the entry's type is `SecureProfileResponse`,
   *   whose `profile` field is a NON-OPTIONAL `PublicProfileResponse`
   *   (`packages/shared-types/src/api/user.ts:154-155`), so a bare `PublicProfileResponse` cannot be
   *   widened into an entry without fabricating its sibling fields — which would make the entry's declared
   *   type a lie, and would feed the fabrication to every other reader of the same key.
   *
   *   D2 — `setProfile(null)` ALSO BLANKS `restrictedActions`, which share the entry, where today it left
   *   them. The direction is toward LESS restriction, so this is NOT safe-by-default and must not be
   *   written up as a harmless side effect.
   *
   * BOTH ARE UNREACHABLE, on the deadness verified above: the only caller is `CallbackHandler.tsx:30-31` /
   * `:34-35`. D2's direction is the SAME privilege hazard design § 7 gives as the reason to DELETE that
   * file rather than consolidate it — it writes `profile` and `screen_name` and never `restrictedActions`,
   * so reviving it by one import would zero `isRestricted` for a restricted user.
   *
   * REJECTED ALTERNATIVE for the null case — clear the CURSOR instead of removing the entry. It avoids the
   * `removeQueries` entirely, which is attractive given design § 1's argument about removals. Cost: a
   * THIRD divergence, since `screen_name` would also go null where today `setProfile(null)` leaves it
   * alone. Fewer divergences wins, and the `removeQueries` hazard is bounded by the same unreachability —
   * the only observer on this key is the permanently disabled Derive_Observer, and no live path reaches
   * this line at all. `exact: true` because the key is the three-element form and this must never behave
   * as a prefix purge; the Personal_Purge is the one place that removes by prefix.
   */
  const setProfile = useCallback(
    (p: PublicProfileResponse | null): void => {
      const key = profileKeys.myProfile(screenNameCursor ?? '')
      if (p === null) {
        queryClient.removeQueries({ queryKey: key, exact: true })
        return
      }
      queryClient.setQueryData<SecureProfileResponse>(key, (prev) => (prev ? { ...prev, profile: p } : prev))
    },
    [queryClient, screenNameCursor],
  )

  // Function declarations should be moved to the top
  const getCurrentSession = useCallback(async (): Promise<Record<string, unknown> | null> => {
    try {
      const session = await fetchAuthSession()
      return session as Record<string, unknown>
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString() || null
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const signIn = useCallback(async (): Promise<void> => {
    try {
      console.log('🔐 [AUTH DEBUG] Starting sign in from origin:', window.location.origin)
      console.log('🔐 [AUTH DEBUG] Current URL:', window.location.href)
      await signInWithRedirect()
    } catch (error: unknown) {
      console.error('🔴 [AUTH ERROR] Sign in failed:', error)
      if (error instanceof Error) {
        console.error('🔴 [AUTH ERROR] Error message:', error.message)
        console.error('🔴 [AUTH ERROR] Error stack:', error.stack)
        setError(error.message)
      }
    }
  }, [])

  const signOut = useCallback(async (): Promise<void> => {
    console.log('🔴 [AUTH DEBUG] Manual sign out initiated')

    // Clear all local state immediately
    setIsAuthenticated(false)
    setUser(null)
    setError(null)
    setAccessToken(null)
    setIDToken(null)
    setSession(null)
    setScreenName(null)
    localStorage.removeItem('fpw_screen_name')

    // Clear Cognito tokens from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key)
      }
    })

    try {
      // Try Amplify signOut with local only (no redirect)
      const { signOut } = await import('aws-amplify/auth')
      await signOut({ global: false })
      console.log('🔴 [AUTH DEBUG] Local signOut successful')
    } catch (error: unknown) {
      console.error('🔴 [AUTH ERROR] Local signOut failed:', error)
      // Continue anyway since we cleared local state
    }

    // Navigate to home page
    navigate('/')
  }, [navigate])

  /**
   * THE TRANSITION EFFECT — where the Personal_Purge actually runs, and it is DELIBERATELY NOT A LINE
   * INSIDE `signOut`. It is placed here, immediately after `signOut`, so the teardown reads in one place
   * even though it now spans a commit boundary.
   *
   * ─── WHY NOT INSIDE `signOut`, WHERE REQUIREMENT 3.3 LITERALLY PUTS IT ──────────────────────────
   *
   * This is a NAMED DEVIATION from Requirement 3.3's letter ("SHALL run as part of the existing `signOut`
   * teardown sequence, alongside the state resets"), argued in design § 1, and it is mechanical rather
   * than stylistic. A later editor reading Requirement 3.3 will otherwise "fix" it back, so:
   *
   * ⚠️ THE OBVIOUS ARGUMENT FOR THIS PLACEMENT OVERREACHES, AND M3 MEASURED THAT. State it the way the
   * measurement leaves it, or the next reader who checks will delete the argument and then the placement.
   *
   * THE MECHANISM IS REAL AND TRANSFERABLE. `@tanstack/query-core` 5.101.3 REFETCHES A REMOVED QUERY as
   * soon as its observer re-renders while still `enabled`. `queryCache.remove()` destroys the query and
   * drops it from the map; the observer still holds the destroyed instance, so on its next render it
   * rebuilds a NEW query for the same hash, making `shouldFetchOptionally`'s `query !== prevQuery` true,
   * `isStale` trivially true for an entry with no data, and `enabled` still true — so the removal itself
   * provokes a fetch. That is measured, not reasoned: against the installed version, with a live `queryFn`
   * and the gate held open, one removal produces one further request and the entry comes back
   * (`AuthContext.transition.test.tsx`'s anti-case).
   *
   * BUT IT IS NOT REACHABLE ON THE REAL `signOut` PATH. `signOut` above issues its seven `setState` calls
   * and its `localStorage` work SYNCHRONOUSLY, before its first suspension point
   * (`await import('aws-amplify/auth')`), so a `removeQueries` placed beside those resets joins ONE batch:
   * the commit React flushes at that await already carries `idToken === null`, every personal query in the
   * app is gated on `!!idToken`, and so every `['me',…]` observer's next render is DISABLED.
   * `shouldFetchOptionally` cannot fire, and nothing refetches. The anti-case reaches the refetch only
   * because it holds the gate open deliberately.
   *
   * WHAT MOVING THE PURGE INTO THE TEARDOWN ACTUALLY BREAKS IS THE WINDOW, not the network. Under M3 the
   * entry is already gone in the render where the gate FIRST closes, because the synchronous
   * `removeQueries` runs before React flushes the reset batch — so Property 2's
   * `transition[firstGateClosed]?.cached` assertion inverts, while its "still absent after settle" half
   * stays GREEN. M3 is still what pins this placement: the literal reading of Requirement 3.3 cannot be
   * restored without a red test. But the red it produces reads "the purge ran before the gate was
   * committed", NOT "the purge provoked a refetch" — do not cite a hazard the real path cannot reach.
   *
   * THE GROUND THAT SURVIVES IS REQUIREMENT 3.8, spelled out two paragraphs below: the "purge even if the
   * sign-out call throws" guarantee holds STRUCTURALLY only in this effect, because the resets that drive
   * it precede `signOut`'s `try` entirely, whereas a line inside the `try` would depend on control flow
   * for the same guarantee.
   *
   * By the time this effect runs, the commit in which `isAuthenticated`, `idToken` and `screen_name` are
   * all cleared has already happened: `NotificationBell` is unmounted (`Navigation.tsx:88` gates it on
   * `screen_name`) and every remaining `['me',…]` observer is `enabled: false`, so neither
   * `shouldLoadOnMount` nor `shouldFetchOptionally` can fire. The purge is inert with respect to the
   * network, and what it removes stays removed.
   *
   * Requirement 3.4 (purge BEFORE `signOut`'s `navigate('/')`) is satisfied with room to spare: this
   * effect fires on the commit produced by the reset batch above, which React flushes at the `await
   * import(...)`, while `navigate('/')` is after both that await and `await signOut({ global: false })`.
   * Requirement 3.8 (purge even when Amplify throws) is satisfied STRUCTURALLY, which a call inside
   * `signOut`'s `try` would not have been: the resets that drive this effect precede that `try` entirely,
   * so the edge is committed before Amplify is ever invoked.
   *
   * ─── WHY A TRANSITION_REF AND NOT `if (!isAuthenticated)` ───────────────────────────────────────
   *
   * The ref holds the PREVIOUS `isAuthenticated`, compared and then updated inside the effect (Req 3.6;
   * the same pattern as `UserRelationsContext.tsx` and `CraftRoomContext.tsx`). The bare form would also
   * fire on the very first mount of an ANONYMOUS session and on EVERY unrelated re-render while signed
   * out, turning a one-shot eviction into a repeated one. Only authenticated→unauthenticated purges.
   *
   * It also covers `initAuth`'s token-expiry catch VACUOUSLY AND CORRECTLY (Req 3.5, design § 2): that
   * path threw before `setIsAuthenticated(true)`, so `isAuthenticated` was never true, so no edge exists
   * and this effect does not fire. No purge is added there, and the absence is a decision: `idToken` is
   * still `null` from its initialiser, every personal query is gated on `!!idToken` so none ever fetched,
   * and the `QueryClient` is built fresh per page load (`index.tsx:28`) — the cache is PROVABLY empty. A
   * line whose only possible effect is on a state the code cannot reach is a line no test can turn red,
   * and an untestable line in a security-shaped teardown reads to the next maintainer as evidence that
   * this path CAN hold personal data. It would also create the second purge site Req 3.1 exists to
   * prevent.
   *
   * ─── THE DERIVE-GUARD: THIS SECTION USED TO ARGUE AGAINST ONE. THAT ARGUMENT IS WITHDRAWN ──────
   *
   * ⚠️ READ THIS BEFORE DELETING THE GUARD IN THE DERIVATION BLOCK ABOVE. There IS a derive-guard in this
   * file — `const cachedOwnProfile = isAuthenticated ? data : undefined` — and it is MANDATORY. This
   * section previously argued that no guard was needed here, and the argument no longer holds.
   *
   * WHAT IT SAID, AND WHY IT WAS RIGHT WHEN IT SAID IT (Req 8.9): slices 7 and 8 pair their purge with a
   * `const x = enabled ? data : undefined` guard because two mechanisms are required and neither is
   * sufficient alone. That rule did not reach this provider, because all nineteen Auth_Surface members
   * derived from `useState` and `signOut` cleared all nine backing slices synchronously — there was no
   * cached payload for a guard to blank. It closed by naming its own expiry condition: THE MOMENT any
   * member reads from the cache, the guard becomes mandatory and the argument is withdrawn (Req 8.10).
   *
   * THAT MOMENT HAS ARRIVED. `profile`, `restrictedActions` and `isRestricted` are no longer `useState`
   * slices; they are derived from the Profile_Entry `['me','profile', <own screen name>]`, which
   * `initAuth`'s establishing write populates. Requirement 8.10's trigger has FIRED, and Requirement 8.9
   * is withdrawn — the premise it rested on ("every member derives from `useState`") is now FALSE of this
   * file. Its argument remains CORRECT for the provider that shipped without the derivation; the
   * withdrawal is not a retraction of that, and nothing about the purge's placement changes.
   *
   * So the pairing rule now applies here in full, and BOTH mechanisms are present: the purge is this
   * effect, and the guard is the line named above. Its load-bearing branch is the token-expiry path in
   * `initAuth`'s outer catch, where the gate closes while the cursor is deliberately left NON-NULL, so the
   * guard — and nothing else — blanks `profile` and `restrictedActions`. The full accounting per teardown
   * path is at the guard itself and in design § 4b; the mutation that removes it is M12.
   *
   * This section is left standing, rewritten rather than deleted, precisely because a maintainer who found
   * the ORIGINAL text next to the guard would have had this file's own authority for deleting the guard.
   */
  const wasAuthenticated = useRef(isAuthenticated)
  useEffect(() => {
    const previouslyAuthenticated = wasAuthenticated.current
    wasAuthenticated.current = isAuthenticated

    if (previouslyAuthenticated && !isAuthenticated) {
      purgePersonalCache(queryClient)
    }
  }, [isAuthenticated, queryClient])

  useEffect(() => {
    registerTokenRefreshCallback((token) => setIDToken(token))
  }, [])

  // On mount: load user and profile
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔐 [AUTH DEBUG] Starting auth initialization...')

        // First check if we have valid tokens
        const session = await fetchAuthSession()
        console.log('🔐 [AUTH DEBUG] Session check:', {
          hasTokens: !!session.tokens,
          hasIdToken: !!session.tokens?.idToken,
          hasAccessToken: !!session.tokens?.accessToken,
        })

        if (!session.tokens?.idToken || !session.tokens?.accessToken) {
          console.log('🔴 [AUTH DEBUG] No valid tokens found - user not authenticated')
          throw new Error('No valid tokens')
        }

        const currentUser = await getCurrentUser()
        console.log('🔐 [AUTH DEBUG] Current user object:', currentUser)
        console.log('🔐 [AUTH DEBUG] User ID for manual lookup:', currentUser.userId)

        // Validate that we have a proper user with userId
        if (!currentUser.userId) {
          console.log('🔴 [AUTH DEBUG] Invalid user object - no userId')
          throw new Error('Invalid user - no userId')
        }

        console.log('🔐 [AUTH DEBUG] Setting user and authenticated state')
        setUser(currentUser)
        setIsAuthenticated(true)
        setError(null)

        // Skip profile fetching on create profile page
        if (window.location.pathname === '/create/profile') {
          console.log('🔐 [AUTH DEBUG] On create profile page, but checking if profile exists...')
          // Don't skip - let's check if user actually has a profile
        }

        // Skip profile fetching on login redirect page
        if (window.location.pathname === '/login-redirect') {
          console.log('🔐 [AUTH DEBUG] On login redirect page, skipping profile fetch')
          return
        }

        // Try to get screen name from localStorage first (fallback for CORS issues)
        const cachedScreenName = readCachedScreenName()
        if (cachedScreenName) {
          console.log('🔐 [AUTH DEBUG] Using cached screen name:', cachedScreenName)
          setScreenName(cachedScreenName)
        }

        // Try to fetch profile
        let loadedProfile: PublicProfileResponse | null = null
        try {
          if (currentUser.userId) {
            console.log('🔐 [AUTH DEBUG] Fetching own secure profile (self-mode) for user ID:', currentUser.userId)
            // Use the idToken from the session already fetched at the top of initAuth.
            const idToken = session.tokens?.idToken?.toString() || ''
            const secureResponse = await fetchOwnSecureProfile(idToken)
            loadedProfile = secureResponse.profile
            if (loadedProfile?.screen_name) {
              setScreenName(loadedProfile.screen_name)
              // Cache screen name for future use
              localStorage.setItem('fpw_screen_name', loadedProfile.screen_name)
              /*
               * THE ESTABLISHING WRITE (Req 4.1, 4.2, 4.5, 4.9). This is the line that makes
               * `['me','profile',<own screen name>]` the SINGLE SOURCE for `profile`,
               * `restrictedActions` and `isRestricted` — the two `useState` slices that used to hold
               * them are gone, so this is the only copy, not a second one.
               *
               * THREE-element key, built by `profileKeys.myProfile` (`queries/profile/keys.ts:126`) and
               * never minted here. The two-element prefix `['me','profile']` would create an entry no
               * query ever reads: `useMyProfilePageQuery` (`queries/profile/queries.ts:94-104`) and the
               * Derive_Observer both observe the three-element form.
               *
               * The WHOLE `SecureProfileResponse`, not `response.profile` and not a projection. That is
               * what `useMyProfilePageQuery`'s own `queryFn` returns (it hands back `fetchSecureProfile`'s
               * full response), and it is what makes the entry sufficient for all three derived members —
               * `permissions` lives on `SecureProfileResponse` and nowhere else
               * (`packages/shared-types/src/api/user.ts:178`). That is why the local
               * `permissions ?? {restricted_actions: [], is_restricted: false}` default that used to sit
               * above this guard is gone rather than kept: the derivation applies the same default at the
               * read, from this payload.
               *
               * SAME-VALUE, not same-shape (Req 4.3): both fetchers reach ONE handler —
               * `lambda-secure/src/index.ts:1911` dispatches the length-1 self-mode route and `:1915` the
               * length-2 by-screen-name route, both to `getSecureProfileHandler`, which computes
               * `isSelfMode` (`get-secure-profile.ts:20`), resolves the caller's own screen name
               * (`:23-29`) or reads it from the path (`:31`), and converges on ONE `fetchProfileData`
               * (`:36-41`) with the same `includePrivatePatterns: true` and `requestingUserId`; `limit`
               * defaults identically at `:33` (`|| 8`) because neither fetcher sends one, and
               * `isOwnProfile` (`:44`) is true either way. If any step of that chain stops holding, the
               * failure no longer produces a wrongly-shaped EXTRA entry — it feeds `restrictedActions` on
               * the auth surface. Re-verify it before editing either fetcher.
               *
               * PLAIN VALUE, not a functional updater (Req 4.5) — per design § 4's per-writer table, and
               * the rule is what the writer WRITES, not how many writers there are (there are three):
               *
               *   | Writer                              | Writes                     | Form                 |
               *   |-------------------------------------|----------------------------|----------------------|
               *   | this establishing write             | the WHOLE response         | plain value          |
               *   | `reloadProfile`'s refresh           | the WHOLE response         | plain value          |
               *   | the `setProfile` Setter_Shim        | ONE field, `profile`       | functional MANDATORY |
               *
               * The two whole-payload writers REPLACE, so last-write-wins is the intended semantics and a
               * functional updater would receive the previous payload only to discard it. The shim patches
               * one field it did not fetch — the slice-8 hazard — so its functional form is not optional.
               *
               * INSIDE the `screen_name` guard on purpose (Req 4.10): the no-`screen_name` arm and both
               * catch arms below cannot reach this line, so no failure — including the 404 — can write a
               * partial entry. Hoisting it above the guard is mutation M8.
               */
              queryClient.setQueryData(profileKeys.myProfile(loadedProfile.screen_name), secureResponse)
              console.log('🔐 [AUTH DEBUG] Loaded and cached profile:', loadedProfile)
            } else {
              // Authenticated user exists but has no profile - redirect to create profile
              console.log('🔄 [AUTH] Authenticated user without profile, redirecting to create profile')
              setScreenName(null)
              if (window.location.pathname !== '/create/profile' && window.location.pathname !== '/login-redirect') {
                navigateRef.current('/create/profile')
              }
            }
          }
        } catch (err: unknown) {
          console.error('🔴 [AUTH ERROR] Profile fetch failed:', err)
          const errorStatus =
            err && typeof err === 'object' && 'status' in err ? (err as { status?: number }).status : undefined
          console.log('🔐 [AUTH DEBUG] Error status:', errorStatus)

          // NOTHING blanks the profile surface here, and that is the point: a failed fetch writes no
          // cache entry (Req 4.10, the establishing write sits inside the success guard), the
          // `QueryClient` is built fresh per page load (`index.tsx:28`) and this effect is mount-only,
          // so there is no entry for this arm to clear. The removed `setProfile(null)` /
          // `setRestrictedActions([])` had nothing left to reset once the two slices ceased to exist.

          // 404 means profile doesn't exist (self-mode returns 404 when the user has not
          // created a profile yet) - redirect to create profile. secureFetch attaches the
          // HTTP status to the thrown error, so this detects the no-profile case the same
          // way the previous public-by-id path did.
          if (errorStatus === 404) {
            console.log('🔄 [AUTH] Profile not found (404), redirecting to create profile')
            setScreenName(null)
            localStorage.removeItem('fpw_screen_name')
            if (window.location.pathname !== '/create/profile' && window.location.pathname !== '/login-redirect') {
              navigateRef.current('/create/profile')
            }
          } else {
            // Network error or other issue - keep cached screen name if available
            console.log('🔐 [AUTH DEBUG] Using cached screen name if available:', cachedScreenName)
            if (cachedScreenName) {
              setScreenName(cachedScreenName)
              console.log('🔐 [AUTH DEBUG] Keeping cached screen name during profile fetch failure')
            } else {
              setScreenName(null)
              console.log('🔐 [AUTH DEBUG] No cached screen name and profile fetch failed - staying on current page')
            }
          }
        }
      } catch (error: unknown) {
        console.log('🔴 [AUTH DEBUG] Auth initialization failed:', error)

        // Check if this is a token expiration vs not logged in
        if (
          error instanceof Error &&
          (error.message.includes('token') ||
            error.message.includes('expired') ||
            error.message.includes('NotAuthorizedException'))
        ) {
          console.log(
            '🔴 [AUTH DEBUG] Token expired/invalid - clearing auth state but preserving screen name temporarily',
          )
          // Clear corrupted Cognito tokens but preserve screen name for better UX
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
              localStorage.removeItem(key)
            }
          })
          // Keep cached screen name so user sees familiar state before re-login
          // It will be cleared on successful re-authentication or manual logout
        } else {
          console.log('🔴 [AUTH DEBUG] User not authenticated - clearing all cached data')
          // Clear everything for genuine "not logged in" state
          localStorage.removeItem('fpw_screen_name')
        }

        setIsAuthenticated(false)
        setUser(null)
        // The removed `setProfile(null)` / `setRestrictedActions([])` are replaced HERE by the
        // Derive_Guard, and by nothing else. This is the one branch where that is true: the cursor is
        // deliberately left NON-NULL two lines down (cached name, for the re-login UX), so re-keying
        // cannot blank the surface the way it does in every other teardown path. What closes the surface
        // is `setIsAuthenticated(false)` above, in the same commit, feeding
        // `cachedOwnProfile = isAuthenticated ? data : undefined`. This is the branch M12 exists for.
        // Keep screen name from cache if token expired (better UX)
        const cachedName = readCachedScreenName()
        setScreenName(cachedName)

        if (error instanceof Error) {
          setError(error.message)
        }
      } finally {
        console.log('🔐 [AUTH DEBUG] Auth initialization complete, isLoading -> false')
        setIsLoading(false)
      }
    }

    initAuth()
    /*
     * `queryClient` IS DELIBERATELY ABSENT, and the resulting `react-hooks/exhaustive-deps` WARNING IS
     * ACCEPTED rather than silenced (no disable directive: the lint gate is warnings-tolerant and the
     * website already carries nine of these). `initAuth` closes over it for the establishing write above.
     * Adding it would change NO behavior — `useQueryClient()` returns the same instance for this provider's
     * lifetime, the client being constructed once at module scope in `index.tsx:28` — and this dep array is
     * `initAuth`'s CONTROL FLOW. "Mount-only" is load-bearing: two of the removed `setState` calls rest on
     * the failure arms finding no prior entry, which holds because this runs once per page load against a
     * fresh client.
     *
     * ⚠️ THAT MOUNT-ONLY CLAIM IS NOW TRUE AND ENFORCED HERE, WHERE IT USED TO BE FALSE. This array read
     * `[getCurrentSession, navigate]`. `getCurrentSession` is a `useCallback` with `[]` and so is stable,
     * which left `navigate` as the only thing that could re-run the effect — and re-run it it did, on every
     * single route change, because react-router 7.18.1 memoises `useNavigate` on `locationPathname` and
     * `routePathnamesJson`. The docblock claimed mount-only while the deps guaranteed the opposite; that was
     * BL-053, and the cost was a full re-authentication plus a `GET /profile?self=true` per navigation.
     *
     * ⚠️ DO NOT PUT `navigate` BACK. The two redirects inside `initAuth` read `navigateRef.current`
     * instead — see the ref's own note above for why that is safe and why it is kept fresh. Re-adding
     * `navigate` here restores the per-navigation refetch, and
     * `AuthContext.seed.test.tsx`'s "`initAuth` runs EXACTLY ONCE across several navigations" test is the
     * assertion that will go red when you do. `getCurrentSession` being the sole remaining dep is what
     * makes this array equivalent to `[]` in practice while staying honest about what the effect closes
     * over.
     */
  }, [getCurrentSession])

  /**
   * ─── THE SESSION-POPULATION EFFECT — NOW FAILS LOUDLY, LIKE `initAuth` ALREADY DID ──────────────
   *
   * The bare `fetchAuthSession()` below REJECTS on a real failure: an expired refresh token, a Cognito
   * outage, a network blip. Until now the body had no `try`/`catch` at all, unlike the `initAuth` effect
   * directly above it, so that rejection became an UNHANDLED one, none of the three setters ran, and
   * `idToken` / `accessToken` / `session` silently retained their previous values with NO error state. Every
   * downstream request then went out with a stale or absent token and failed for an unrelated-looking
   * reason — which is exactly the failure this shape is worst at surfacing.
   *
   * The `catch` is `initAuth`'s, copied rather than invented: `if (error instanceof Error) setError(...)`.
   * The three `null` writes are the other half — the point is that a failed session read must not leave a
   * STALE token behind for the next request to use. `getCurrentSession` cannot throw (it catches and
   * returns `null` itself); the raw `fetchAuthSession()` above it can.
   *
   * The `cancelled` flag is NOT copied from `initAuth`, which deliberately has none (see Requirement 4.7
   * at the abort-ref below). It is here because this effect, unlike that one, RE-RUNS: `isAuthenticated`
   * flipping false→true→false can leave a read in flight whose resolution would write a token back after
   * the gate closed. On React 19 a post-unmount `setState` is a silent no-op rather than a warning, so the
   * unmount case is not the motivating one — the re-run case is.
   */
  useEffect(() => {
    let cancelled = false

    const fetchSessionData = async (): Promise<void> => {
      try {
        const token = await fetchAuthSession()
        const sess = await getCurrentSession()
        if (cancelled) return

        setIDToken(token.tokens?.idToken?.toString() || null)
        setAccessToken(token.tokens?.accessToken?.toString() || null)
        setSession(sess)
      } catch (error: unknown) {
        if (cancelled) return
        console.log('🔴 [AUTH DEBUG] Session population failed:', error)

        // Do not leave a stale token in place for the next request to use.
        setIDToken(null)
        setAccessToken(null)
        setSession(null)

        if (error instanceof Error) {
          setError(error.message)
        }
      }
    }

    if (isAuthenticated) {
      fetchSessionData()
    }

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, getCurrentSession]) // Add getCurrentSession to the dependency array

  /**
   * THE REFRESH CONTROLLER — context-owned, one in flight at a time, and aborted on unmount.
   *
   * ⚠️ IT IS NOT REACT QUERY'S SIGNAL, AND THAT IS A NAMED DEVIATION (Requirement 6.5, design § 5). The
   * letter of 6.5 says "forward React Query's `signal`", which means `queryClient.fetchQuery`. That is
   * IMPOSSIBLE HERE, and the reason is the key shape rather than a preference: the personal profile key is
   * SCREEN-NAME-PARAMETERISED (`queries/profile/keys.ts:126`) while a SELF-MODE refresh does not learn the
   * screen name until its own response arrives — which, in the post-creation case, is the entire point of
   * the call. Keying on the CURRENT `screen_name` writes `['me','profile','']` in exactly the case that
   * matters (the "entry no query ever reads" defect Requirement 4.2 names); minting a self-mode key is
   * forbidden by Requirement 4.2; forking on whether the cursor is null gives two write paths, forbidden
   * by Requirement 6.9. So the residual is in the signal's PROVENANCE, not in its effect: the outcome 6.5
   * exists for — an aborted refresh becoming a CANCELLATION rather than an error, Requirement 9.1's second
   * intentional delta — is fully achieved, and the fetcher change is the additive `opts` Requirement 10.3
   * anticipates. The key-shape question is recorded as an open question, not fixed here.
   *
   * The unmount abort is the other half: without it a refresh outliving the provider would resolve into
   * `setScreenName` on an unmounted tree. `initAuth` is deliberately NOT given a controller — Requirement
   * 4.7 leaves that half open, because threading one through it is control flow, which Requirement 1.3
   * forbids.
   */
  const reloadProfileAbortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    return () => {
      reloadProfileAbortRef.current?.abort()
    }
  }, [])

  /**
   * ─── `reloadProfile` — AN ABORT-AWARE DIRECT FETCH THAT WRITES THE SAME ENTRY (design § 5) ───────
   *
   * FIVE STEPS INSTEAD OF SEVEN. The NAME, the POSITION in the value object below and the
   * `() => Promise<void>` TYPE are unchanged, so `CreateProfile.tsx:18` and `:81` — the only caller
   * anywhere — need no edit (Requirement 6.2):
   *
   *   1. `if (!user) return` — PRESERVED (Requirement 6.6). A refresh with no authenticated user issues
   *      no request.
   *   2. abort any refresh still in flight, through the controller ref above.
   *   3. `const idToken = (await getIdToken()) || ''` — UNCHANGED, see below.
   *   4. `await fetchOwnSecureProfile(idToken, { signal })` — the fetcher's additive, CONDITIONALLY
   *      spread `opts`, so `initAuth`'s signal-less call still issues byte-identically the request it
   *      issues today (Requirement 10.3).
   *   5. `setQueryData` onto the SAME three-element entry the establishing write targets — keyed
   *      correctly because the screen name has now arrived — then the preserved cursor advance.
   *
   * WHAT WAS REMOVED: the four `setState` calls (`setProfile` twice, `setRestrictedActions` twice) and
   * the local `permissions ?? {restricted_actions: [], is_restricted: false}` default, whose backing
   * slices ceased to exist when the derivation block above took over. ONE cursor write now does what
   * three `setState` calls did. `setScreenName(loadedProfile?.screen_name || null)` and the failure arm's
   * `setScreenName(null)` are PRESERVED VERBATIM.
   *
   * ⚠️ THE SUCCESS ARM'S `setScreenName` IS LOAD-BEARING TWICE OVER (Requirement 6.11), which is why it
   * must not be read as a leftover: it is `screen_name`'s fallback AND it ADVANCES THE CURSOR so the
   * Derive_Observer re-keys onto the entry step 5 just wrote. Without it the write is invisible to the
   * surface in exactly the post-creation case that matters, because `initAuth`'s 404 arm left the cursor
   * NULL — the entry would hold the payload while all three derived members stayed blank, and
   * `CreateProfile.tsx:81`'s `await` would resolve on a surface that never updated.
   *
   * ⚠️ DEVIATION, STATED HERE SO IT IS NOT "FIXED" LATER: THIS IS NOT `invalidateQueries` + `refetch`.
   * The Derive_Observer is permanently `enabled: false` and `isActive()` reads `observer.options.enabled`
   * (`@tanstack/query-core@5.101.3 build/modern/query.js:97-101`), so a `type: 'active'` refetch refreshes
   * nothing BY CONSTRUCTION — not because the profile page happens to be unmounted. In the
   * brand-new-profile case the entry does not even exist, because `initAuth`'s fetch 404'd and
   * Requirement 4.10 forbids writing on failure. The awaited promise would resolve WITH NO PAYLOAD
   * FETCHED, and the damage is downstream and silent: `CreateProfile.tsx:83` navigates using its own
   * local `screenName.trim()` so the URL looks right, while `EditProfile.tsx:61` early-returns on a falsy
   * `loggedInScreenName` and its OWNERSHIP CHECK SIMPLY DOES NOT RUN (`:60-65`). A live guard hole, not
   * theoretical staleness. M9 is this alternative.
   *
   * ⚠️ `getIdToken()` STAYS AS THE TOKEN SOURCE (Requirement 6.7, design § 6). Reading the `idToken` state
   * member is simpler and saves a `fetchAuthSession` round trip, and it is safe for today's only caller
   * BY ACCIDENT: `CreateProfile` passes `idToken` to `createProfile` on the line BEFORE
   * `await reloadProfile()`, so a null token would already have thrown into its `catch`. That accident is
   * why it is the wrong thing to depend on — the member is set by an effect ordered against
   * `isAuthenticated`, not against an arbitrary caller, so a second caller added anywhere inherits a
   * silent failure mode: empty token → 401 → the discriminating catch reads it as a GENUINE failure and
   * runs `setScreenName(null)`, which under the derivation blanks `profile`, `screen_name` AND
   * `restrictedActions` in one step, signing the user out of the UI with no message and no log beyond
   * `secureFetch`'s. Requirement 6.4 forbids adding an error surface, so there is nowhere for that to
   * become visible. `getIdToken()` re-reads the live Amplify session and cannot be stale by construction.
   * The token is an ARGUMENT TO THE FETCHER and never part of a query key.
   *
   * ⚠️ THE FAILURE ARM PERFORMS ZERO CACHE WRITES AND NO REMOVAL. That keeps Property 6's anti-case
   * (`dataUpdateCount` UNCHANGED, which distinguishes "no write" from "written then reverted to the same
   * value") exact, and avoids issuing a `removeQueries` while other observers may still be enabled —
   * design § 1's rejected shape. The orphaned entry is unobserved once the cursor no longer names it,
   * holds authoritative server data rather than anything corrupt, and is evicted by the Personal_Purge at
   * sign-out.
   *
   * ⚠️ ONE PRE-EXISTING BEHAVIOR IS PRESERVED, NOT FIXED, AND IS NAMED BECAUSE IT READS AS A REGRESSION:
   * the failure arm leaves `restrictedActions` EMPTY, hence `isRestricted === false`, so A FAILED REFRESH
   * UN-RESTRICTS A RESTRICTED USER IN THE UI. Today's bare `catch` does exactly that by calling
   * `setRestrictedActions([])`; the cursor-null derivation reproduces it exactly. Not introduced here and
   * not fixed here (Requirement 9.7); recorded as requirements open question 10.
   *
   * REQUIREMENT 6.8 HOLDS BY CONSTRUCTION, in its amended form. Steps 3–5 are sequential inside one
   * `async` function, so at the instant the promise resolves `getQueryData(profileKeys.myProfile(sn))`
   * HOLDS the payload and the cursor advance is ENQUEUED — neither depends on a React commit, so both are
   * assertable synchronously in the awaited continuation. Visibility then follows for free: the three
   * members are pure functions of the entry and the cursor, and React 18+ batches the cursor update with
   * `CreateProfile`'s own `navigate` in the same continuation, so the commit that mounts `EditProfile`
   * already carries the new `screen_name`.
   */
  const reloadProfile = useCallback(async (): Promise<void> => {
    if (!user) return

    // Step 2. One refresh in flight at a time. An abort is NOT an error here — see the catch.
    reloadProfileAbortRef.current?.abort()
    const controller = new AbortController()
    reloadProfileAbortRef.current = controller

    try {
      const idToken = (await getIdToken()) || ''
      const secureResponse = await fetchOwnSecureProfile(idToken, { signal: controller.signal })
      const loadedProfile = secureResponse.profile
      /*
       * THE SAME THREE-ELEMENT ENTRY the establishing write in `initAuth` targets, built by
       * `profileKeys.myProfile` and never minted here — one entry, not two (Requirement 6.10), and no
       * third write path (Requirement 6.9). The WHOLE `SecureProfileResponse` goes in, because
       * `permissions` lives on it and nowhere else, and as a PLAIN VALUE per design § 4's per-writer
       * table, reproduced at this call site because Requirement 4.5's conditional otherwise reads as
       * unmet:
       *
       *   | Writer                              | Writes                     | Form                 |
       *   |-------------------------------------|----------------------------|----------------------|
       *   | `initAuth`'s establishing write     | the WHOLE response         | plain value          |
       *   | THIS refresh                        | the WHOLE response         | plain value          |
       *   | the `setProfile` Setter_Shim        | ONE field, `profile`       | functional MANDATORY |
       *
       * This one REPLACES an authoritative payload wholesale, so last-write-wins is what a refresh MEANS;
       * a functional updater would receive the previous payload only to discard it. Only the shim patches
       * a single field of a payload it did not fetch, which is the slice-8 hazard the functional form
       * exists for.
       *
       * WHERE THE RESPONSE CARRIES NO `screen_name`, NOTHING IS WRITTEN: the key would be
       * `['me','profile','']`, the "entry no query ever reads" defect Requirement 4.2 names.
       *
       * The guard is the OPTIONAL CHAIN even though `SecureProfileResponse.profile` is declared
       * NON-OPTIONAL (`packages/shared-types/src/api/user.ts:154-155`). Deliberate: the declared type is a
       * compile-time claim about a payload that came off the network, the next line — preserved verbatim
       * from today's code — already treats it as possibly absent, and `initAuth`'s establishing write uses
       * the identical `if (loadedProfile?.screen_name)` shape. Trusting the declaration here would key the
       * entry `['me','profile','undefined']`, which is strictly worse than writing nothing.
       */
      if (loadedProfile?.screen_name) {
        queryClient.setQueryData(profileKeys.myProfile(loadedProfile.screen_name), secureResponse)
      }
      setScreenName(loadedProfile?.screen_name || null)
    } catch (err: unknown) {
      /*
       * THE CATCH DISCRIMINATES, where today's is bare. An abort is a CANCELLATION: it touches NEITHER
       * state NOR cache, so a superseded refresh cannot blank the surface a live one is about to fill,
       * and it is not reported as a failure. That is Requirement 9.1's second intentional delta, and the
       * whole reason the controller exists.
       *
       * The name is read structurally rather than through `instanceof DOMException`, matching this file's
       * existing narrowing idiom for `errorStatus` in `initAuth`'s profile catch: `secureFetch` does not
       * wrap or re-throw, so the rejection arrives from `fetch` exactly as issued, and a structural read
       * is indifferent to whether the host's `DOMException` sits on `Error.prototype`.
       *
       * ANY OTHER FAILURE runs the preserved `setScreenName(null)`, which nulls the cursor and thereby
       * blanks all three derived members at once — NO new error state, NO new user-visible message
       * (Requirement 6.4), and NO cache write or removal.
       */
      const errorName = err && typeof err === 'object' && 'name' in err ? (err as { name?: string }).name : undefined
      if (errorName === 'AbortError') return
      setScreenName(null)
    }
    /*
     * `queryClient` IS IN THE DEPS and `setProfile` IS NOT — the second half matters. `setProfile` is no
     * longer referenced here at all (its two calls were among the four removals), so there is no
     * dependency to declare; adding it would be adding a dep for a name the body does not use.
     * `queryClient` is referenced, is stable for the provider's lifetime (`index.tsx:28` builds it once),
     * and declaring it changes no behavior — unlike `initAuth`'s array, which Requirement 1.3 freezes.
     * `setScreenName` is a `useState` setter, which eslint already knows is stable.
     */
  }, [user, getIdToken, queryClient])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        error,
        accessToken,
        idToken,
        session,
        screen_name,
        setScreenName,
        profile,
        setProfile,
        restrictedActions,
        isRestricted,
        signIn,
        signOut,
        getIdToken,
        getAccessToken,
        getCurrentSession,
        reloadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
